import React, { useState } from "react";
import "../App.css";
import axios from "axios";
import FormItem from "./FormItem";
import { Button, Card, Row, Col } from "react-bootstrap";
import { EXAMPLE_FILE, OCR_ENDPOINT, ReceiptItem } from "../common";
import _ from "lodash";

interface OCRModalProps {
  addItem: (text: string, price: number, people: string[]) => void;
}

/**
 * Takes the user through a multi-step OCR process to extract line items from a receipt.
 *
 * Will also render the inferred line items after the OCR finishes up.
 *
 * Both "add" and "delete" functionalities exist because the OCR is quite shitty right now and need manual correction from the user.
 */
const OCRModal: React.FC<OCRModalProps> = ({ addItem }) => {
  const [fileObject, setFileObject] = useState<File | null>(null);
  const [fileBlob, setFileBlob] = useState<string>(EXAMPLE_FILE);
  const [fileUploaded, setFileUploaded] = useState<boolean>(false);
  const [ocrStep, setOcrStep] = useState<number>(1);
  const [receiptData, setReceiptData] = useState<ReceiptItem[]>([]);
  const [removedItemIndexes, setRemovedItemIndexes] = useState<number[]>([]);

  const uploadFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFileUploaded(true);
    setRemovedItemIndexes([]);

    if (e.target.files) {
      const fileObject = e.target.files[0];
      setFileObject(fileObject);
      setFileBlob(URL.createObjectURL(fileObject));
    }
  };

  // Call OCR API and set state here
  const doOcr = async () => {
    if (!fileObject) {
      return;
    }

    const fileData = new FormData();
    fileData.append("file", fileObject, fileObject.name);

    const response = await axios.post(OCR_ENDPOINT, fileData);

    const items = response.data.items.map((item: string[], index: number) => ({
      initialName: item[0],
      initialCost: item[1],
      index,
    }));

    setReceiptData(items);
    setOcrStep(3);
  };

  const processReceiptData = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setOcrStep(2);
    await doOcr();
  };

  // Create a "banned" set of indexes we don't want to show, for simplicity (but don't drop it from the data itself)
  const removeFromReceiptData = (itemIndex: number) => {
    const newRemovedIndexes = _.uniq([...removedItemIndexes, itemIndex]);
    setRemovedItemIndexes(newRemovedIndexes);
  };

  return (
    <div className="column align-items-center my-1">
      {ocrStep === 1 && (
        <form onSubmit={processReceiptData}>
          <div className="form-group">
            <input
              type="file"
              className="form-control"
              id="fileUpload"
              onChange={uploadFile}
            />
          </div>
          <div className="form-group">
            <div className="my-3">
              <img
                className="receipt-preview"
                src={fileBlob}
                alt={"Receipt Preview"}
              />
            </div>
          </div>
          <p>
            {" "}
            <small>
              <i>
                <b>Mobile users:</b> please give your browser{" "}
                <a href="https://help.yahoo.com/kb/SLN36087.html">
                  camera permissions
                </a>
              </i>
              <p className="mt-2">
                We don't store receipt data on our servers and we don't
                guarantee perfect itemization.
              </p>
            </small>{" "}
          </p>
          <button
            disabled={!fileUploaded}
            className="btn btn-primary modal-button"
          >
            Next
          </button>
        </form>
      )}
      {ocrStep === 2 && (
        <div>
          <center className="mb-3">
            {" "}
            <small> Please wait for receipt to process... </small>{" "}
          </center>
          <img
            className={"modal-button"}
            src="https://i.pinimg.com/originals/fb/7a/1f/fb7a1f8c4b21618ea4e15297d9bc7789.gif"
            alt="Loading"
          />
        </div>
      )}
      {ocrStep === 3 && (
        <div>
          <center>
            {receiptData.length === removedItemIndexes.length && (
              <p>
                <i>No items available, feel free to close this window.</i>
              </p>
            )}
            {receiptData.length > removedItemIndexes.length && (
              <p>Scan finished, please verify the items look good:</p>
            )}
          </center>
          <ul className="no-left-padding">
            {_.reject(receiptData, (item) =>
              removedItemIndexes.includes(item.index)
            ).map((item: ReceiptItem) => (
              <Card key={item.index} className={"mt-3 my-2 p-2 bg-light"}>
                <FormItem
                  addItem={(text: string, price: number, people: string[]) => {
                    removeFromReceiptData(item.index);
                    addItem(text, price, people);
                  }}
                  initialName={item.initialName}
                  initialCost={item.initialCost}
                />
                <div className="mt-2 mb-3">
                  <Row>
                    <Col className="d-flex">
                      <Button
                        className="mx-2 modal-button"
                        variant="danger"
                        onClick={() => removeFromReceiptData(item.index)}
                      >
                        Delete
                      </Button>
                    </Col>
                  </Row>
                </div>
              </Card>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OCRModal;
